/*
  KneePillow pregnancy tools (13 Oct 2026): due date, weeks pregnant, IVF due date and UK maternity leave.

  One engine for the four /pages/*-calculator pages and the quick calculator on /collections/pregnancy-pillows.
  Dates are whole UTC day numbers, so clock changes never move a date.

  Rules and sources (re-check every April, when SMP and the lower earnings limit change):
  - Due date: 280 days from the first day of the last period, adjusted for cycle length; 266 days from conception;
    IVF transfer + 266 - embryo age in days; scan date + 280 - gestation at the scan.
  - Trimesters follow the NHS week-by-week guide: 1-12, 13-27, 28 onwards.
  - Antenatal timings: NHS "Your antenatal appointments" (booking 8-12 wk, scans 11-14 and 18-21 wk, 16/25/28/36/41 wk).
  - Side sleeping from 28 weeks: NHS/Tommy's. Pillows are offered for comfort only, never as a safety measure.
  - Maternity leave (gov.uk/maternity-pay-leave): expected week of childbirth (EWC) runs Sunday to Saturday; earliest start
    11 weeks before the EWC; leave starts automatically after pregnancy-related absence in the 4 weeks before the EWC;
    tell the employer by the end of the 15th week before the EWC (the qualifying week); MATB1 no more than 20 weeks
    before the due date; 52 weeks of leave; SMP for 39 weeks: 90% of average weekly earnings for 6 weeks, then the lower of
    the flat rate or 90%; earnings must average at least the lower earnings limit.
*/
(function (root) {
  'use strict';

  var DAY = 86400000;
  var SMP = { year: '2026/27', rate: 194.32, lel: 129 };

  function parse(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
    if (!m) return null;
    var y = +m[1], mo = +m[2] - 1, d = +m[3];
    var t = Date.UTC(y, mo, d);
    var check = new Date(t);
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo || check.getUTCDate() !== d) return null;
    return Math.round(t / DAY);
  }
  function iso(n) { return new Date(n * DAY).toISOString().slice(0, 10); }
  function weekday(n) { return new Date(n * DAY).getUTCDay(); }
  function today() {
    var d = new Date();
    return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY);
  }

  var engine = {
    SMP: SMP,
    parse: parse,
    iso: iso,
    fromPeriod: function (lmp, cycle) {
      var c = Math.round(Number(cycle) || 28);
      if (c < 20) c = 20;
      if (c > 45) c = 45;
      return lmp + 280 + (c - 28);
    },
    fromConception: function (day) { return day + 266; },
    fromIvf: function (transfer, embryoDays) { return transfer + 266 - (Number(embryoDays) || 0); },
    fromScan: function (scan, weeks, days) { return scan + 280 - ((Number(weeks) || 0) * 7 + (Number(days) || 0)); },
    // Gestation on a given day: completed weeks + days since the (equivalent) first day of the last period.
    gestation: function (edd, on) {
      var total = 280 - (edd - on);
      var weeks = Math.floor(total / 7);
      return { total: total, weeks: weeks, days: total - weeks * 7 };
    },
    trimester: function (total) { return total < 91 ? 1 : total < 196 ? 2 : 3; },
    dayAt: function (edd, weeks, days) { return edd - 280 + weeks * 7 + (days || 0); },
    maternity: function (edd, leaveStart, weeklyEarnings) {
      var ewc = edd - weekday(edd);
      var qw = ewc - 105;
      var out = {
        ewcStart: ewc, ewcEnd: ewc + 6,
        qwStart: qw, qwEnd: qw + 6,
        tellBy: qw + 6,
        matb1From: edd - 140,
        earliestLeave: ewc - 77,
        autoStartFrom: ewc - 28
      };
      if (leaveStart != null) {
        out.leaveStart = leaveStart;
        out.tooEarly = leaveStart < out.earliestLeave;
        out.smpNoticeBy = leaveStart - 28;
        out.ordinaryEnd = leaveStart + 182 - 1;
        out.smpEnd = leaveStart + 273 - 1;
        out.leaveEnd = leaveStart + 364 - 1;
        out.returnDay = leaveStart + 364;
      }
      var awe = Number(weeklyEarnings);
      if (weeklyEarnings !== '' && weeklyEarnings != null && isFinite(awe) && awe > 0) {
        var first = Math.round(awe * 0.9 * 100) / 100;
        var later = Math.min(SMP.rate, first);
        out.pay = {
          eligible: awe >= SMP.lel,
          first: first,
          later: later,
          total: Math.round((first * 6 + later * 33) * 100) / 100
        };
      }
      return out;
    },
    // Ovulation (NHS: usually 12-14 days before the next period; sperm can live up to 7 days). Cycles are
    // projected forward from the last period so the current one contains the given day.
    ovulation: function (lmp, cycle, on, count) {
      var c = Math.round(Number(cycle) || 28);
      if (c < 21) c = 21;
      if (c > 35) c = 35;
      var k = on > lmp ? Math.floor((on - lmp) / c) : 0;
      var out = [];
      for (var i = 0; i < (count || 3); i++) {
        var start = lmp + (k + i) * c, next = start + c;
        out.push({ start: start, next: next, ovFrom: next - 14, ovTo: next - 12, fertileFrom: next - 19, fertileTo: next - 12, testFrom: next, due: engine.fromPeriod(start, c) });
      }
      return out;
    },
    // UK antenatal and work milestones for one due date, in date order.
    milestones: function (edd) {
      var m = engine.maternity(edd);
      var at = function (w, d) { return engine.dayAt(edd, w, d || 0); };
      var list = [
        { id: 'booking', from: at(8), to: at(12), title: 'Booking appointment with your midwife', note: 'Usually between 8 and 12 weeks. Contact your midwife or GP surgery as soon as you know you are pregnant.' },
        { id: 'dating', from: at(11), to: at(14), title: 'Dating scan (the "12-week scan")', note: 'Between 11 and 14 weeks. It can change your due date.' },
        { id: 'w16', from: at(16), title: '16-week appointment', note: 'Your screening results, and blood pressure and urine checks.' },
        { id: 'anomaly', from: at(18), to: at(21), title: 'Screening scan (the "20-week scan")', note: 'Between 18 and 21 weeks.' },
        { id: 'matb1', from: m.matb1From, title: 'MATB1 certificate available', note: 'Your midwife or GP can give it no more than 20 weeks before your due date. Your employer needs it for maternity pay.' },
        { id: 'tell', from: m.tellBy, title: 'Tell your employer by this date', note: 'The end of the 15th week before the week your baby is due: your due date and when you want leave to start.' },
        { id: 'w25', from: at(25), title: '25-week appointment', note: 'Offered if this is your first baby.' },
        { id: 'sleep', from: at(28), title: 'Go to sleep on your side', note: 'From 28 weeks, UK guidance is to go to sleep on your side, for night-time sleep and daytime naps.', product: true },
        { id: 'w28', from: at(28), title: '28-week appointment', note: 'Blood tests, and an anti-D injection if you need one.' },
        { id: 'leave', from: m.earliestLeave, title: 'Earliest maternity leave start', note: '11 weeks before the week your baby is due.' },
        { id: 'w36', from: at(36), title: '36-week appointment', note: 'Your midwife checks the baby’s position.' },
        { id: 'due', from: edd, title: 'Your due date', note: 'Pregnancy usually lasts between 37 and 42 weeks.' }
      ];
      return list.sort(function (a, b) { return a.from - b.from; });
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  root.KPPregTools = engine;
  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- UI */

  var STORE_KEY = 'kpPregDue';
  var fmtLong, fmtShort;
  try {
    fmtLong = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
    fmtShort = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  } catch (e) { fmtLong = fmtShort = null; }
  function long(n) { return fmtLong ? fmtLong.format(new Date(n * DAY)) : iso(n); }
  function short(n) { return fmtShort ? fmtShort.format(new Date(n * DAY)) : iso(n); }
  var fmtDM = null;
  try { fmtDM = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { /* fall back to ISO */ }
  function dmy(n) { return fmtDM ? fmtDM.format(new Date(n * DAY)) + ' ' + new Date(n * DAY).getUTCFullYear() : iso(n); }
  function range(a, b) {
    var sameYear = new Date(a * DAY).getUTCFullYear() === new Date(b * DAY).getUTCFullYear();
    return (sameYear && fmtDM ? fmtDM.format(new Date(a * DAY)) : dmy(a)) + ' – ' + dmy(b);
  }
  function money(v) { return '£' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  function age(g) { return plural(g.weeks, 'week', 'weeks') + (g.days ? ' and ' + plural(g.days, 'day', 'days') : ''); }
  var TRI = ['', 'First trimester', 'Second trimester', 'Third trimester'];

  function saveDue(edd) { try { window.localStorage.setItem(STORE_KEY, iso(edd)); } catch (e) { /* private mode */ } }
  function savedDue() {
    try { return parse(window.localStorage.getItem(STORE_KEY)); } catch (e) { return null; }
  }
  function track(tool) {
    try { if (typeof window.gtag === 'function') window.gtag('event', 'preg_tool_calculate', { tool: tool }); } catch (e) { /* analytics optional */ }
  }

  function progress(g) {
    var pct = Math.max(0, Math.min(100, g.total / 280 * 100));
    return '<div class="kpt-progress" role="img" aria-label="' + esc(age(g)) + ' of about 40 weeks">' +
      '<span class="kpt-progress__seg"></span><span class="kpt-progress__seg"></span><span class="kpt-progress__seg"></span>' +
      '<span class="kpt-progress__dot" style="left:' + pct.toFixed(1) + '%"></span></div>' +
      '<div class="kpt-progress__labels"><span>1st</span><span>2nd</span><span>3rd</span></div>';
  }

  function productNudge(el) {
    var p = el.__kptProduct;
    if (!p || !p.url) return '';
    return '<a class="kpt-nudge" href="' + esc(p.url) + '">' +
      (p.image ? '<img src="' + esc(p.image) + '" width="56" height="56" alt="" loading="lazy">' : '') +
      '<span><strong>' + esc(p.line) + '</strong><span>' + esc(p.title) + (p.price ? ' · ' + esc(p.price) : '') + '</span></span>' +
      '<span class="kpt-nudge__go" aria-hidden="true">→</span></a>';
  }

  // One cycle as a Monday-first calendar: period, fertile days, likely ovulation, today.
  function calendar(c, plen, now) {
    var lead = (weekday(c.start) + 6) % 7, cells = '';
    for (var b = 0; b < lead; b++) cells += '<span class="kpt-cal__d is-blank"></span>';
    for (var d = c.start; d < c.next; d++) {
      var cls = d - c.start < plen ? 'is-period' : (d >= c.ovFrom && d <= c.ovTo ? 'is-ov' : (d >= c.fertileFrom && d <= c.fertileTo ? 'is-fertile' : ''));
      cells += '<span class="kpt-cal__d ' + cls + (d === now ? ' is-today' : '') + '"><b>' + new Date(d * DAY).getUTCDate() + '</b></span>';
    }
    return '<div class="kpt-cal" aria-hidden="true"><div class="kpt-cal__head"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div><div class="kpt-cal__grid">' + cells + '</div>' +
      '<div class="kpt-cal__key"><span class="is-period">Period</span><span class="is-fertile">Fertile</span><span class="is-ov">Ovulation likely</span><span class="is-today">Today</span></div></div>';
  }

  function timeline(el, edd, now) {
    var items = engine.milestones(edd);
    var nextSet = false;
    var html = items.map(function (it) {
      var end = it.to != null ? it.to : it.from;
      var state = end < now ? 'past' : (it.from <= now ? 'now' : 'next');
      if (state === 'next' && !nextSet) { nextSet = true; state = 'next is-first'; }
      var when = it.to != null ? range(it.from, it.to) : short(it.from);
      return '<li class="kpt-step is-' + state + '"><span class="kpt-step__dot" aria-hidden="true"></span>' +
        '<div class="kpt-step__body"><span class="kpt-step__when">' + esc(when) + '</span>' +
        '<strong>' + esc(it.title) + '</strong><span class="kpt-step__note">' + esc(it.note) + '</span>' +
        (it.product && end >= now ? productNudge(el) : '') + '</div></li>';
    }).join('');
    return '<div class="kpt-timeline"><h2 class="kpt-h2">Your UK pregnancy timeline</h2><div class="kpt-timeline__grid"><ol class="kpt-steps">' + html + '</ol>' + feature(el, edd, now) + '</div></div>';
  }

  // Desktop: the pillow beside the timeline (phones get the smaller card inside the 28-week step instead).
  function feature(el, edd, now) {
    var p = el.__kptProduct;
    if (!p || !p.url) return '';
    var w28 = engine.dayAt(edd, 28);
    var kicker = w28 > now ? 'From 28 weeks \u00b7 ' + short(w28) : 'Third trimester comfort';
    var stars = p.reviews > 0 ? '\u2605 ' + p.rating + ' (' + p.reviews + ' reviews) \u00b7 ' : '';
    return '<aside class="kpt-feature">' + (p.big ? '<img src="' + esc(p.big) + '" width="360" height="360" alt="" loading="lazy">' : '') +
      '<div class="kpt-feature__body"><p class="kpt-eyebrow">' + esc(kicker) + '</p><p class="kpt-feature__title">' + esc(p.line) + '</p>' +
      '<p class="kpt-feature__meta">' + esc(p.title) + '</p><p class="kpt-feature__meta">' + esc(stars + (p.price || '')) + '</p>' +
      '<a class="kpt-btn kpt-btn--wide" href="' + esc(p.url) + '">See the pillow</a></div></aside>';
  }

  function links(el, edd) {
    var d = iso(edd);
    var out = [];
    var map = el.__kptLinks || {};
    if (el.dataset.kpPregTool !== 'maternity' && map.maternity) out.push('<a class="kpt-btn kpt-btn--ghost" href="' + esc(map.maternity) + '?due=' + d + '">Work out my maternity leave dates</a>');
    if (el.dataset.kpPregTool !== 'weeks' && map.weeks) out.push('<a class="kpt-btn kpt-btn--ghost" href="' + esc(map.weeks) + '?due=' + d + '">How pregnant will I be on a date?</a>');
    if (el.dataset.kpPregTool === 'maternity' && map.due) out.push('<a class="kpt-btn kpt-btn--ghost" href="' + esc(map.due) + '?due=' + d + '">See my full pregnancy timeline</a>');
    return out.length ? '<div class="kpt-actions">' + out.join('') + '</div>' : '';
  }

  function summary(edd, now, extra) {
    var g = engine.gestation(edd, now);
    var head;
    if (g.total < 0) head = '<p class="kpt-result__line">These dates are for a pregnancy that has not started yet.</p>';
    else if (g.total > 294) head = '<p class="kpt-result__line">This due date has passed. If you have had your baby, congratulations.</p>';
    else head = '<p class="kpt-result__line"><strong>' + esc(age(g)) + ' pregnant</strong> today · ' + TRI[engine.trimester(g.total)] +
      (edd >= now ? ' · ' + plural(edd - now, 'day', 'days') + ' to go' : '') + '</p>' + progress(g);
    return '<p class="kpt-eyebrow">Your estimated due date</p><p class="kpt-result__big">' + esc(long(edd)) + '</p>' + head + (extra || '');
  }

  function errorBox(msg) { return '<p class="kpt-error" role="alert">' + esc(msg) + '</p>'; }

  function val(form, name) { var f = form.elements[name]; return f ? f.value : ''; }

  function compute(el, form) {
    var tool = el.dataset.kpPregTool;
    var now = today();
    var mode = val(form, 'mode') || 'period';
    var edd = null, extra = '', err = '';

    if (tool === 'due' || tool === 'weeks' || tool === 'quick') {
      if (mode === 'period') {
        var lmp = parse(val(form, 'lmp'));
        if (lmp == null) err = 'Enter the first day of your last period.';
        else if (lmp > now) err = 'That date is in the future. Enter the first day of your last period.';
        else if (now - lmp > 300) err = 'That is more than 42 weeks ago. Check the date, or use your due date instead.';
        else {
          var cycle = val(form, 'cycle') || 28;
          edd = engine.fromPeriod(lmp, cycle);
          extra = '<p class="kpt-result__meta">Estimated conception: around ' + esc(long(edd - 266)) + '</p>';
        }
      } else if (mode === 'conception') {
        var c = parse(val(form, 'conception'));
        if (c == null) err = 'Enter your conception date.';
        else if (c > now) err = 'That date is in the future.';
        else if (now - c > 290) err = 'That is more than 42 weeks ago. Check the date.';
        else edd = engine.fromConception(c);
      } else if (mode === 'scan') {
        var s = parse(val(form, 'scan'));
        var sw = Number(val(form, 'scan_weeks')), sd = Number(val(form, 'scan_days')) || 0;
        if (s == null) err = 'Enter the date of your scan.';
        else if (s > now) err = 'Enter a scan that has already happened.';
        else if (!(sw >= 5 && sw <= 40)) err = 'Enter how many weeks pregnant the scan said you were.';
        else edd = engine.fromScan(s, sw, sd);
      } else if (mode === 'due') {
        edd = parse(val(form, 'due'));
        if (edd == null) err = 'Enter your due date.';
        else if (edd - now > 300 || now - edd > 60) { err = 'Check the due date: it should be within the next 42 weeks.'; edd = null; }
      }
    } else if (tool === 'ivf') {
      var t = parse(val(form, 'transfer'));
      var embryo = Number(val(form, 'embryo'));
      if (t == null) err = 'Enter your embryo transfer date.';
      else if (now - t > 290) err = 'That is more than 42 weeks ago. Check the date.';
      else if (t - now > 365) err = 'Enter a transfer date within the next year.';
      else {
        edd = engine.fromIvf(t, embryo);
        extra = '<p class="kpt-result__meta">On transfer day you count as ' + esc(age(engine.gestation(edd, t))) + ' pregnant. Weeks are counted as if your last period began on ' + esc(long(edd - 280)) + '.</p>';
      }
    } else if (tool === 'maternity') {
      edd = parse(val(form, 'due'));
      if (edd == null) err = 'Enter your due date.';
      else if (edd - now > 300 || now - edd > 120) { err = 'Check the due date: it should be within the next 42 weeks.'; edd = null; }
    }

    if (tool === 'ovulation') {
      var lp = parse(val(form, 'lmp'));
      var cyc = Math.round(Number(val(form, 'cycle')) || 28);
      var plen = Math.round(Number(val(form, 'period_len')) || 5);
      var o = el.querySelector('[data-kpt-result]'), t2 = el.querySelector('[data-kpt-timeline]');
      if (lp == null) { o.innerHTML = errorBox('Enter the first day of your last period.'); return; }
      if (lp > now) { o.innerHTML = errorBox('That date is in the future. Enter the first day of your last period.'); return; }
      if (now - lp > 365) { o.innerHTML = errorBox('Enter a period from the last 12 months.'); return; }
      var cy = engine.ovulation(lp, cyc, now, 3);
      var cur = cy[0];
      track(tool);
      o.innerHTML = '<p class="kpt-eyebrow">Your most fertile days</p><p class="kpt-result__big">' + esc(range(cur.fertileFrom, cur.fertileTo)) + '</p>' +
        '<p class="kpt-result__line">Ovulation likely <strong>' + esc(range(cur.ovFrom, cur.ovTo)) + '</strong> \u00b7 next period about ' + esc(short(cur.next)) + '</p>' +
        calendar(cur, plen, now) +
        '<p class="kpt-result__meta">Pregnancy test: most work from the first day of a missed period, ' + esc(long(cur.testFrom)) + '.</p>' +
        '<div class="kpt-actions">' + (el.__kptLinks && el.__kptLinks.due ? '<a class="kpt-btn kpt-btn--ghost" href="' + esc(el.__kptLinks.due) + '?lmp=' + iso(cur.start) + '">If you conceive this cycle: due date</a>' : '') + '</div>';
      side(el, '<div class="kpt-card kpt-cycles"><p class="kpt-eyebrow">Your next three cycles</p>' + cy.map(function (x, n) {
        return '<div class="kpt-cycle"><strong>' + (n ? 'Period from ' : 'This cycle: period from ') + esc(short(x.start)) + '</strong>' +
          '<span>Most fertile ' + esc(range(x.fertileFrom, x.fertileTo)) + '</span><span>Ovulation likely ' + esc(range(x.ovFrom, x.ovTo)) + '</span><span>Test from ' + esc(short(x.testFrom)) + '</span></div>';
      }).join('') + '</div>');
      if (t2) t2.innerHTML = '';
      balance(el);
      return;
    }

    var out = el.querySelector('[data-kpt-result]');
    var tl = el.querySelector('[data-kpt-timeline]');
    if (err || edd == null) {
      side(el, '');
      out.innerHTML = errorBox(err || 'Check the dates and try again.');
      if (tl) tl.innerHTML = '';
      return;
    }
    saveDue(edd);
    track(tool);

    if (tool === 'quick') {
      var gq = engine.gestation(edd, now);
      out.innerHTML = summary(edd, now) + (gq.total >= 0 && gq.total < 196 ? '<p class="kpt-result__meta">Side sleeping is advised from 28 weeks: ' + esc(long(engine.dayAt(edd, 28))) + '.</p>' : '') +
        (el.__kptLinks && el.__kptLinks.due ? '<div class="kpt-actions"><a class="kpt-btn" href="' + esc(el.__kptLinks.due) + '?due=' + iso(edd) + '">See my full UK timeline</a></div>' : '');
      return;
    }

    if (tool === 'weeks') {
      var on = parse(val(form, 'on')) || now;
      var g = engine.gestation(edd, on);
      var rows = [12, 20, 28, 37, 40].map(function (w) {
        return '<tr><th scope="row">' + w + ' weeks</th><td>' + esc(long(engine.dayAt(edd, w))) + '</td></tr>';
      }).join('');
      var big = g.total < 0 ? 'Not pregnant yet' : age(g);
      var line = g.total < 0 ? 'On ' + long(on) + ' you would not be pregnant yet by these dates.' :
        (on === now ? 'Today you are in week ' + (g.weeks + 1) + ' · ' + TRI[engine.trimester(g.total)] :
          'On ' + long(on) + ' you will be in week ' + (g.weeks + 1) + ' · ' + TRI[engine.trimester(Math.max(0, g.total))]);
      out.innerHTML = '<p class="kpt-eyebrow">' + (on === now ? 'You are' : 'On that date you will be') + '</p><p class="kpt-result__big">' + esc(big) + '</p>' +
        '<p class="kpt-result__line">' + esc(line) + '</p>' + (g.total >= 0 ? progress(g) : '') +
        '<p class="kpt-result__meta">Estimated due date: <strong>' + esc(long(edd)) + '</strong>' + (edd >= now ? ' (' + plural(edd - now, 'day', 'days') + ' to go)' : '') + '</p>' +
        '<table class="kpt-table"><caption>Key weeks for you</caption><tbody>' + rows + '</tbody></table>' + links(el, edd);
      if (tl) tl.innerHTML = timeline(el, edd, now);
      return;
    }

    if (tool === 'maternity') {
      var ls = parse(val(form, 'leave_start'));
      var m = engine.maternity(edd, ls, val(form, 'earnings'));
      var r = function (label, value, hint) {
        return '<tr><th scope="row">' + esc(label) + (hint ? '<span>' + esc(hint) + '</span>' : '') + '</th><td>' + esc(value) + '</td></tr>';
      };
      var html = '<p class="kpt-eyebrow">Your maternity leave dates</p><p class="kpt-result__big">Tell your employer by ' + esc(long(m.tellBy)) + '</p>' +
        '<p class="kpt-result__line">Due ' + esc(long(edd)) + ' · earliest leave ' + esc(long(m.earliestLeave)) + '</p>' +
        '<table class="kpt-table"><tbody>' +
        r('Expected week of childbirth', short(m.ewcStart) + ' – ' + short(m.ewcEnd), 'Sunday to Saturday') +
        r('Qualifying week', short(m.qwStart) + ' – ' + short(m.qwEnd), '15th week before the due week') +
        r('Tell your employer by', long(m.tellBy), 'Due date and planned start date') +
        r('MATB1 available from', long(m.matb1From), 'From your midwife or GP') +
        r('Earliest leave start', long(m.earliestLeave), '11 weeks before the due week') +
        r('Leave starts automatically from', long(m.autoStartFrom), 'If you are off with a pregnancy-related illness') +
        '</tbody></table>';
      if (ls != null) {
        html += '<h3 class="kpt-h3">If your leave starts ' + esc(long(ls)) + '</h3>';
        if (m.tooEarly) html += errorBox('That is before the earliest start date (' + long(m.earliestLeave) + ').');
        html += '<table class="kpt-table"><tbody>' +
          r('Give notice for maternity pay by', long(m.smpNoticeBy), 'At least 28 days before, with your MATB1') +
          r('Ordinary leave ends', long(m.ordinaryEnd), 'First 26 weeks') +
          r('Maternity pay ends', long(m.smpEnd), 'Up to 39 weeks') +
          r('Full 52 weeks end', long(m.leaveEnd)) +
          r('Back at work', long(m.returnDay), 'If you take all 52 weeks') +
          '</tbody></table>';
      }
      if (m.pay) {
        html += '<h3 class="kpt-h3">Statutory Maternity Pay estimate (' + SMP.year + ' rates)</h3>';
        if (!m.pay.eligible) {
          html += '<p class="kpt-result__meta">Average earnings under ' + money(SMP.lel) + ' a week do not qualify for SMP. You may be able to claim Maternity Allowance instead.</p>';
        } else {
          html += '<table class="kpt-table"><tbody>' +
            r('Weeks 1–6', money(m.pay.first) + ' a week', '90% of your average weekly earnings') +
            r('Weeks 7–39', money(m.pay.later) + ' a week', money(SMP.rate) + ' or 90%, whichever is lower') +
            r('Total over 39 weeks', money(m.pay.total), 'Before tax and National Insurance') +
            '</tbody></table>';
        }
      }
      out.innerHTML = html + links(el, edd);
      if (tl) tl.innerHTML = '';
      side(el, feature(el, edd, now));
      balance(el);
      return;
    }

    // due + ivf
    out.innerHTML = summary(edd, now, extra) + links(el, edd);
    if (tl) tl.innerHTML = timeline(el, edd, now);
    balance(el);
  }

  // Desktop: the result card is at least as tall as the form (content centred, no stretched card).
  // The side slot sits under the form on desktop and under the result on phones: the pillow card on the
  // maternity page, the next three cycles on the ovulation page.
  function side(el, html) {
    var slot = el.querySelector('[data-kpt-side]');
    if (slot) slot.innerHTML = html || '';
  }
  var wide = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
  function balance(el) {
    var out = el.querySelector('.kpt-grid [data-kpt-result]');
    var form = el.querySelector('.kpt-grid form[data-kpt-form]');
    if (!out || !form) return;
    out.style.minHeight = '';
    if (wide && wide.matches) out.style.minHeight = form.offsetHeight + 'px';
  }

  function setMode(el, form) {
    var mode = val(form, 'mode') || 'period';
    var groups = el.querySelectorAll('[data-kpt-mode]');
    for (var i = 0; i < groups.length; i++) {
      var on = groups[i].getAttribute('data-kpt-mode').split(' ').indexOf(mode) !== -1;
      groups[i].hidden = !on;
      var inputs = groups[i].querySelectorAll('input,select');
      for (var j = 0; j < inputs.length; j++) inputs[j].disabled = !on;
    }
  }

  function init(el) {
    if (el.__kptReady) return;
    el.__kptReady = true;
    var form = el.querySelector('form[data-kpt-form]');
    if (!form) return;
    try { el.__kptProduct = JSON.parse((el.querySelector('[data-kpt-product]') || {}).textContent || 'null'); } catch (e) { el.__kptProduct = null; }
    try { el.__kptLinks = JSON.parse((el.querySelector('[data-kpt-links]') || {}).textContent || '{}'); } catch (e) { el.__kptLinks = {}; }

    var max = iso(today());
    var dates = form.querySelectorAll('input[type="date"][data-kpt-past]');
    for (var i = 0; i < dates.length; i++) dates[i].max = max;

    // Prefill: ?due= / ?lmp= from another tool, else the due date saved on this device.
    var params = {};
    try { params = Object.fromEntries(new URLSearchParams(window.location.search)); } catch (e) { params = {}; }
    var due = parse(params.due) || savedDue();
    var prefilled = false;
    if (parse(params.lmp) != null && form.elements.lmp) {
      form.elements.lmp.value = params.lmp;
      prefilled = true;
    } else if (due != null && form.elements.due) {
      if (form.elements.mode && form.querySelector('[name="mode"][value="due"]')) form.querySelector('[name="mode"][value="due"]').checked = true;
      form.elements.due.value = iso(due);
      prefilled = true;
    }

    setMode(el, form);
    syncDates(form);
    form.addEventListener('input', function () { syncDates(form); });
    balance(el);
    if (wide && wide.addEventListener) wide.addEventListener('change', function () { balance(el); });
    // Maternity: one-tap leave start dates, worked out from the due date.
    form.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-kpt-leave]') : null;
      if (!b) return;
      var due = parse(val(form, 'due'));
      if (due == null) {
        el.querySelector('[data-kpt-result]').innerHTML = errorBox('Enter your due date first, then pick a start date.');
        return;
      }
      var pick = b.getAttribute('data-kpt-leave');
      var day = pick === 'earliest' ? engine.maternity(due).earliestLeave : due - Number(pick);
      form.elements.leave_start.value = iso(day);
      syncDates(form);
      el.__kptDone = true;
      compute(el, form);
    });
    form.addEventListener('change', function (e) {
      syncDates(form);
      if (e.target && e.target.name === 'mode') setMode(el, form);
      if (el.__kptDone) compute(el, form);
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      el.__kptDone = true;
      compute(el, form);
      var out = el.querySelector('[data-kpt-result]');
      if (out && window.matchMedia && window.matchMedia('(max-width: 989px)').matches && out.scrollIntoView) {
        out.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    if (prefilled) { el.__kptDone = true; compute(el, form); }
  }

  // iPhone/iPad Safari shows an empty date field with no format hint or icon, and sizes it wider than
  // its box. The kpt-ios class switches on our icon and "Choose a date" hint (kp-preg-tools.css).
  function syncDates(form) {
    var wraps = form.querySelectorAll('.kpt-date');
    for (var i = 0; i < wraps.length; i++) {
      var input = wraps[i].querySelector('input');
      wraps[i].classList.toggle('is-empty', !input || !input.value);
    }
  }

  function boot() {
    try {
      var nav = window.navigator || {};
      if (/iP(hone|ad|od)/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) {
        document.documentElement.classList.add('kpt-ios');
      }
    } catch (e) { /* detection is optional */ }
    var els = document.querySelectorAll('[data-kp-preg-tool]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window !== 'undefined' ? window : globalThis);
